import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Switch from "react-switch";
import Swal from "sweetalert2";
import "./rules.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken } from "../../sessionSlice";
import { useNavigate } from "react-router-dom";
import ColorPallet from "../color_pallet/colorPallet";
import { updateThemeData } from "../../functions/updateThemeData";
import { clearAdminDirty, setAdminDirty } from "../../adminDirty";
import TextStyleControls, { ensureGoogleFont } from "../../components/TextStyleControls";
import {
  emptySectionStyles,
  ensureFontsFromSectionStyles,
  parseSectionStyles,
} from "../../../utils/sectionStyles";

const TITLE_MAX_LEN = 80;
const MIN_UPLOAD_COUNT = 1;
const MAX_UPLOAD_COUNT = 10;
const MIN_BADGE_SHARE_LIMIT = 1;
const MAX_BADGE_SHARE_LIMIT = 10;
const MIN_VOTES_PER_CATEGORY = 1;
const MAX_VOTES_PER_CATEGORY = 20;

const MIN_FONT_SIZE_PX = 1;
const MAX_FONT_SIZE_PX = 200;

function normalizeFontSizeInput(value, fallback) {
  if (value === "" || value == null) return fallback;
  const n = Number.parseInt(String(value), 10);
  if (!Number.isFinite(n) || n <= 0) return fallback;
  return Math.max(MIN_FONT_SIZE_PX, Math.min(MAX_FONT_SIZE_PX, n));
}

function clampUploadCount(value) {
  const n = Number.parseInt(String(value ?? ""), 10);
  if (!Number.isFinite(n)) return MIN_UPLOAD_COUNT;
  return Math.max(MIN_UPLOAD_COUNT, Math.min(MAX_UPLOAD_COUNT, n));
}

function clampBadgeShareLimit(value) {
  const n = Number.parseInt(String(value ?? ""), 10);
  if (!Number.isFinite(n)) return MIN_BADGE_SHARE_LIMIT;
  return Math.max(MIN_BADGE_SHARE_LIMIT, Math.min(MAX_BADGE_SHARE_LIMIT, n));
}

function clampVotesPerCategory(value) {
  const n = Number.parseInt(String(value ?? ""), 10);
  if (!Number.isFinite(n)) return MIN_VOTES_PER_CATEGORY;
  return Math.max(MIN_VOTES_PER_CATEGORY, Math.min(MAX_VOTES_PER_CATEGORY, n));
}

function parseStatusOpen(raw, defaultOpen = true) {
  const s = String(raw ?? (defaultOpen ? "open" : "closed")).trim().toLowerCase();
  return s !== "closed" && s !== "0" && s !== "false";
}

function parseBoolTrue(raw, defaultTrue = true) {
  const s = String(raw ?? (defaultTrue ? "true" : "false")).trim().toLowerCase();
  if (defaultTrue) return s !== "false" && s !== "0" && s !== "closed";
  return s === "true" || s === "1" || s === "open";
}

const RULE_MAX_LEN = 200;
const RULE_MAX_COUNT = 7;

const RULE_GROUPS = [
  {
    key: "submit_your_talent_rules",
    titleKey: "submit_your_talent_title",
    label: "Submit your talent",
    styleId: "submit_your_talent",
  },
  {
    key: "expolre_and_react_rules",
    titleKey: "expolre_and_react_title",
    label: "Explore and react",
    styleId: "expolre_and_react",
  },
  {
    key: "cast_your_vote_rules",
    titleKey: "cast_your_vote_title",
    label: "Cast your vote",
    styleId: "cast_your_vote",
  },
];

const emptyRuleTitles = () =>
  Object.fromEntries(RULE_GROUPS.map(({ titleKey, label }) => [titleKey, label]));

const DEFAULT_GAME_UPLOAD_RULES = [
  "Choose your talent category — music, dance, art, comedy, photography, cooking, or other.",
  "Upload a video, image, or written description of your talent.",
  "Add a title and a short description to help the audience connect.",
  "Hit submit — your entry goes to the talent gallery!",
];

function asRuleList(value) {
  if (Array.isArray(value) && value.length) {
    return value.map((item) => String(item ?? ""));
  }
  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed) && parsed.length) {
        return parsed.map((item) => String(item ?? ""));
      }
    } catch {
      /* keep fallback */
    }
  }
  return [""];
}

const emptyRuleGroups = () =>
  Object.fromEntries(RULE_GROUPS.map(({ key }) => [key, [""]]));

const Rules = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme, data } = useSelector((state) => state.theme);

  const [ruleGroups, setRuleGroups] = useState(emptyRuleGroups);
  const [ruleTitles, setRuleTitles] = useState(emptyRuleTitles);
  const [gameUploadRules, setGameUploadRules] = useState(() => [...DEFAULT_GAME_UPLOAD_RULES]);

  const [thankYouPage, setThankYouPage] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [votingOpen, setVotingOpen] = useState(true);
  const [votesPerCategory, setVotesPerCategory] = useState(MIN_VOTES_PER_CATEGORY);
  const [galleryDisplay, setGalleryDisplay] = useState(true);
  const [mosaicEnabled, setMosaicEnabled] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(true);
  const [uploadCount, setUploadCount] = useState(MIN_UPLOAD_COUNT);
  // Which yoga setting is currently saving: 'voting' | 'gallery' | 'mosaic' | 'votes' | 'upload' | null
  const [savingSetting, setSavingSetting] = useState(null);
  const yogaSettingsHydratedRef = useRef(false);

  const [badgesShareStatus, setBadgesShareStatus] = useState(true);
  const [badgesShareMultiple, setBadgesShareMultiple] = useState(false);
  const [badgesShareLimit, setBadgesShareLimit] = useState(MIN_BADGE_SHARE_LIMIT);
  const [badgesShareLimitError, setBadgesShareLimitError] = useState("");

  const [sectionStyles, setSectionStyles] = useState(() => emptySectionStyles());

  const [colors, setColors] = useState({
    text_color: "#000000",
    button_color: "#ffffff",
    button_backgroundcolor: "#000000",
    quiz_question_text_color: "#1a1a1a",
    quiz_question_background_color: "#ffffff",
    quiz_option_text_color: "#0d1b5c",
    quiz_option_background_color: "#eceff1",
    rules_text_color: "#333333",
  });

  const markDirty = useCallback(() => {
    if (!yogaSettingsHydratedRef.current) return;
    setAdminDirty(true, "Rules");
  }, []);

  useEffect(() => {
    return () => clearAdminDirty();
  }, []);

  useEffect(() => {
    if (!data) return;
    const c = data?.colors;
    const flat = data && !c && (data.text_color != null || data.button_color != null);
    const source = c || (flat ? data : null);
    setColors((prev) => ({
      text_color: (source?.text_color ?? data.text_color) ?? prev.text_color,
      button_color: (source?.button_color ?? data.button_color) ?? prev.button_color,
      button_backgroundcolor:
        (source?.button_backgroundcolor ?? data.button_backgroundcolor) ?? prev.button_backgroundcolor,
      quiz_question_text_color:
        ("quiz_question_text_color" in data
          ? data.quiz_question_text_color
          : prev.quiz_question_text_color) ?? prev.quiz_question_text_color,
      quiz_question_background_color:
        ("quiz_question_background_color" in data
          ? data.quiz_question_background_color
          : prev.quiz_question_background_color) ?? prev.quiz_question_background_color,
      quiz_option_text_color:
        ("quiz_option_text_color" in data ? data.quiz_option_text_color : prev.quiz_option_text_color) ??
        prev.quiz_option_text_color,
      quiz_option_background_color:
        ("quiz_option_background_color" in data
          ? data.quiz_option_background_color
          : prev.quiz_option_background_color) ?? prev.quiz_option_background_color,
      rules_text_color:
        data.rules_text_color ??
        source?.text_color ??
        data.text_color ??
        prev.rules_text_color,
    }));

    const nextStyles = parseSectionStyles(data.section_styles, {
      font_family: data.font_family,
      font_size_desktop: data.font_size_desktop,
      font_size_mobile: data.font_size_mobile,
      rules_text_color: data.rules_text_color,
      text_color: data.text_color,
    });
    setSectionStyles(nextStyles);
    ensureFontsFromSectionStyles(nextStyles, ensureGoogleFont);
  }, [data]);

  const handleColorChange = (key) => (e) => {
    setColors((prev) => ({ ...prev, [key]: e.target.value }));
    markDirty();
  };

  const updateSectionStyle = (styleId, part, nextStyle) => {
    // Keep draft values as typed (including empty) so admin can clear and retype.
    setSectionStyles((prev) => ({
      ...prev,
      [styleId]: {
        ...prev[styleId],
        [part]: {
          ...nextStyle,
        },
      },
    }));
    if (nextStyle?.font_family) ensureGoogleFont(nextStyle.font_family);
    markDirty();
  };

  const commitSectionFontSize = (styleId, part, sizeKey) => {
    setSectionStyles((prev) => {
      const block = prev[styleId]?.[part] || {};
      const fallback = sizeKey === "font_size_desktop" ? 16 : 14;
      const next = normalizeFontSizeInput(block[sizeKey], fallback);
      return {
        ...prev,
        [styleId]: {
          ...prev[styleId],
          [part]: {
            ...block,
            [sizeKey]: next,
          },
        },
      };
    });
    markDirty();
  };

  const normalizeAllSectionStyles = useCallback((styles) => {
    const defaults = emptySectionStyles();
    const out = {};
    for (const id of Object.keys(defaults)) {
      const block = styles?.[id] || defaults[id];
      out[id] = {
        title: {
          ...(block.title || defaults[id].title),
          font_size_desktop: normalizeFontSizeInput(
            block.title?.font_size_desktop,
            defaults[id].title.font_size_desktop
          ),
          font_size_mobile: normalizeFontSizeInput(
            block.title?.font_size_mobile,
            defaults[id].title.font_size_mobile
          ),
        },
        rules: {
          ...(block.rules || defaults[id].rules),
          font_size_desktop: normalizeFontSizeInput(
            block.rules?.font_size_desktop,
            defaults[id].rules.font_size_desktop
          ),
          font_size_mobile: normalizeFontSizeInput(
            block.rules?.font_size_mobile,
            defaults[id].rules.font_size_mobile
          ),
        },
      };
    }
    return out;
  }, []);

  // fetch questions & theme on mount
  useEffect(() => {
    yogaSettingsHydratedRef.current = false;
    dispatch(fetchThemeData({ themeId: currentTheme }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;
    setRuleGroups((prev) => {
      const next = { ...prev };
      for (const { key } of RULE_GROUPS) {
        if (key in data) next[key] = asRuleList(data[key]);
      }
      return next;
    });
    setRuleTitles((prev) => {
      const next = { ...prev };
      for (const { titleKey, label } of RULE_GROUPS) {
        if (titleKey in data) {
          next[titleKey] = String(data[titleKey] ?? label).slice(0, TITLE_MAX_LEN);
        }
      }
      return next;
    });
    if ("game_upload_rules" in data) {
      const list = asRuleList(data.game_upload_rules);
      setGameUploadRules(list.length ? list : [...DEFAULT_GAME_UPLOAD_RULES]);
    } else if ("submit_your_talent_rules" in data) {
      // Migrate older themes that only had how-to-play submit rules
      const list = asRuleList(data.submit_your_talent_rules);
      if (list.length && list.some((r) => r.trim())) {
        setGameUploadRules(list);
      }
    }
    // Only overwrite when the key exists on the payload. If fetchThemeData returns a
    // partial object (missing keys), do not use undefined → "" or we wipe a filename
    // just set by upload and abort the in-flight S3 image (NS_BINDING_ABORTED).
    if ("custom_text_thank_you_page" in data) {
      setThankYouPage(data.custom_text_thank_you_page ?? "");
    }
    if ("voting_status" in data) setVotingOpen(parseStatusOpen(data.voting_status, true));
    if ("votes_per_category" in data) {
      setVotesPerCategory(clampVotesPerCategory(data.votes_per_category));
    }
    if ("gallery_display" in data) {
      setGalleryDisplay(parseBoolTrue(data.gallery_display, true));
    }
    if ("mosaic_enabled" in data) {
      setMosaicEnabled(parseBoolTrue(data.mosaic_enabled, false));
    }
    if ("badges_share_status" in data) {
      setBadgesShareStatus(parseStatusOpen(data.badges_share_status, true));
    }
    if ("badges_share_multiple" in data) {
      setBadgesShareMultiple(String(data.badges_share_multiple) === "true");
    }
    if ("badges_share_limit" in data) {
      setBadgesShareLimit(clampBadgeShareLimit(data.badges_share_limit));
      setBadgesShareLimitError("");
    }
    if ("upload_status" in data) setUploadOpen(parseStatusOpen(data.upload_status, true));
    if ("upload_count" in data) setUploadCount(clampUploadCount(data.upload_count));
    yogaSettingsHydratedRef.current = true;
  }, [data]);

  const buildYogaSettingsData = useCallback(
    (overrides = {}) => {
      const vOpen = overrides.votingOpen ?? votingOpen;
      const uOpen = overrides.uploadOpen ?? uploadOpen;
      const uCount = overrides.uploadCount ?? uploadCount;
      const gDisplay = overrides.galleryDisplay ?? galleryDisplay;
      const mEnabled = overrides.mosaicEnabled ?? mosaicEnabled;
      const votes =
        overrides.votesPerCategory != null
          ? clampVotesPerCategory(overrides.votesPerCategory)
          : clampVotesPerCategory(votesPerCategory);
      return {
        voting_status: vOpen ? "open" : "closed",
        upload_status: uOpen ? "open" : "closed",
        upload_count: uOpen ? clampUploadCount(uCount) : MIN_UPLOAD_COUNT,
        gallery_display: gDisplay ? "true" : "false",
        mosaic_enabled: mEnabled ? "true" : "false",
        votes_per_category: votes,
      };
    },
    [votingOpen, uploadOpen, uploadCount, galleryDisplay, mosaicEnabled, votesPerCategory]
  );

  const persistYogaSettings = useCallback(
    async (overrides = {}, settingKey = null) => {
      if (!currentTheme || !adminToken) {
        Swal.fire("No theme", "Select a theme before changing yoga settings.", "warning");
        return false;
      }
      if (settingKey) setSavingSetting(settingKey);
      try {
        await updateThemeData({
          payload: {
            data: buildYogaSettingsData(overrides),
            currentTheme,
          },
          token: adminToken,
        });
        await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
        return true;
      } catch (e) {
        Swal.fire("Error", e.message || "Could not save yoga settings.", "error");
        await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
        return false;
      } finally {
        setSavingSetting(null);
      }
    },
    [adminToken, buildYogaSettingsData, currentTheme, dispatch]
  );

  const handleVotingChange = async (checked) => {
    setVotingOpen(checked);
    if (!yogaSettingsHydratedRef.current) return;
    const ok = await persistYogaSettings({ votingOpen: checked }, "voting");
    if (!ok) setVotingOpen(!checked);
  };

  const handleGalleryDisplayChange = async (checked) => {
    setGalleryDisplay(checked);
    if (!yogaSettingsHydratedRef.current) return;
    const ok = await persistYogaSettings({ galleryDisplay: checked }, "gallery");
    if (!ok) setGalleryDisplay(!checked);
  };

  const handleMosaicEnabledChange = async (checked) => {
    setMosaicEnabled(checked);
    if (!yogaSettingsHydratedRef.current) return;
    const ok = await persistYogaSettings({ mosaicEnabled: checked }, "mosaic");
    if (!ok) setMosaicEnabled(!checked);
  };

  const handleUploadChange = async (checked) => {
    setUploadOpen(checked);
    if (!yogaSettingsHydratedRef.current) return;
    const ok = await persistYogaSettings({ uploadOpen: checked }, "upload");
    if (!ok) setUploadOpen(!checked);
  };

  const handleUploadCountChange = (value) => {
    if (value === "") {
      setUploadCount("");
      return;
    }
    const parsed = Number.parseInt(value, 10);
    if (!Number.isFinite(parsed)) return;
    setUploadCount(parsed);
  };

  const handleUploadCountBlur = async () => {
    if (!uploadOpen || !yogaSettingsHydratedRef.current) return;
    if (uploadCount === "") {
      setUploadCount(MIN_UPLOAD_COUNT);
      await persistYogaSettings({ uploadCount: MIN_UPLOAD_COUNT }, "upload");
      return;
    }
    const clamped = clampUploadCount(uploadCount);
    setUploadCount(clamped);
    await persistYogaSettings({ uploadCount: clamped }, "upload");
  };

  // --- rules handlers ---
  const handleRuleChange = (groupKey, idx, val) => {
    let newVal = val;
    if (val.length > RULE_MAX_LEN) {
      Swal.fire(
        "Limit Exceeded",
        `Rules can be at most ${RULE_MAX_LEN} characters. Extra text has been trimmed.`,
        "warning"
      );
      newVal = val.slice(0, RULE_MAX_LEN);
    }
    setRuleGroups((prev) => {
      const arr = [...(prev[groupKey] || [""])];
      arr[idx] = newVal;
      return { ...prev, [groupKey]: arr };
    });
    markDirty();
  };

  const handleAddRule = (groupKey) => {
    const rules = ruleGroups[groupKey] || [""];
    if (rules.length >= RULE_MAX_COUNT) {
      return Swal.fire(
        "Limit Reached",
        `You can only add up to ${RULE_MAX_COUNT} rules per section.`,
        "warning"
      );
    }
    setRuleGroups((prev) => ({
      ...prev,
      [groupKey]: [...(prev[groupKey] || [""]), ""],
    }));
    markDirty();
  };

  const handleRemoveRule = (groupKey, idx) => {
    const rules = ruleGroups[groupKey] || [""];
    if (rules.length <= 1) {
      return Swal.fire(
        "At Least One Rule Required",
        "You must have at least one rule in each section.",
        "warning"
      );
    }
    setRuleGroups((prev) => ({
      ...prev,
      [groupKey]: (prev[groupKey] || []).filter((_, i) => i !== idx),
    }));
    markDirty();
  };

  const handleGameUploadRuleChange = (idx, val) => {
    let newVal = val;
    if (val.length > RULE_MAX_LEN) {
      Swal.fire(
        "Limit Exceeded",
        `Rules can be at most ${RULE_MAX_LEN} characters. Extra text has been trimmed.`,
        "warning"
      );
      newVal = val.slice(0, RULE_MAX_LEN);
    }
    setGameUploadRules((prev) => {
      const arr = [...prev];
      arr[idx] = newVal;
      return arr;
    });
    markDirty();
  };

  const handleAddGameUploadRule = () => {
    if (gameUploadRules.length >= RULE_MAX_COUNT) {
      return Swal.fire(
        "Limit Reached",
        `You can only add up to ${RULE_MAX_COUNT} rules.`,
        "warning"
      );
    }
    setGameUploadRules((prev) => [...prev, ""]);
    markDirty();
  };

  const handleRemoveGameUploadRule = (idx) => {
    if (gameUploadRules.length <= 1) {
      return Swal.fire(
        "At Least One Rule Required",
        "You must have at least one upload rule.",
        "warning"
      );
    }
    setGameUploadRules((prev) => prev.filter((_, i) => i !== idx));
    markDirty();
  };

  const handleRuleTitleChange = (titleKey, val) => {
    let next = String(val ?? "");
    if (next.length > TITLE_MAX_LEN) {
      Swal.fire(
        "Limit Exceeded",
        `Section title can be at most ${TITLE_MAX_LEN} characters. Extra text has been trimmed.`,
        "warning"
      );
      next = next.slice(0, TITLE_MAX_LEN);
    }
    setRuleTitles((prev) => ({ ...prev, [titleKey]: next }));
    markDirty();
  };

  const handleBadgeShareLimitChange = (value) => {
    if (value === "") {
      setBadgesShareLimit("");
      setBadgesShareLimitError(
        `Enter a number between ${MIN_BADGE_SHARE_LIMIT} and ${MAX_BADGE_SHARE_LIMIT}.`
      );
      markDirty();
      return;
    }
    const parsed = Number.parseInt(value, 10);
    if (!Number.isFinite(parsed)) return;

    if (parsed > MAX_BADGE_SHARE_LIMIT) {
      const msg = `Maximum upload limit is ${MAX_BADGE_SHARE_LIMIT}.`;
      setBadgesShareLimit(MAX_BADGE_SHARE_LIMIT);
      setBadgesShareLimitError(
        `Upload limit must be between ${MIN_BADGE_SHARE_LIMIT} and ${MAX_BADGE_SHARE_LIMIT}.`
      );
      Swal.fire("Upload limit exceeded", msg, "warning");
      markDirty();
      return;
    }

    setBadgesShareLimit(parsed);
    if (parsed < MIN_BADGE_SHARE_LIMIT) {
      setBadgesShareLimitError(
        `Upload limit must be between ${MIN_BADGE_SHARE_LIMIT} and ${MAX_BADGE_SHARE_LIMIT}.`
      );
    } else {
      setBadgesShareLimitError("");
    }
    markDirty();
  };

  const handleBadgeShareLimitBlur = () => {
    const parsed = Number.parseInt(String(badgesShareLimit ?? ""), 10);
    if (
      !Number.isFinite(parsed) ||
      parsed < MIN_BADGE_SHARE_LIMIT ||
      parsed > MAX_BADGE_SHARE_LIMIT
    ) {
      const msg = `Upload limit must be between ${MIN_BADGE_SHARE_LIMIT} and ${MAX_BADGE_SHARE_LIMIT}.`;
      setBadgesShareLimitError(msg);
      Swal.fire("Invalid upload limit", msg, "warning");
      setBadgesShareLimit(clampBadgeShareLimit(badgesShareLimit));
      return;
    }
    setBadgesShareLimitError("");
    setBadgesShareLimit(parsed);
  };

  const handleVotesPerCategoryChange = (value) => {
    if (value === "") {
      setVotesPerCategory("");
      return;
    }
    const parsed = Number.parseInt(value, 10);
    if (!Number.isFinite(parsed)) return;
    setVotesPerCategory(parsed);
  };

  const handleVotesPerCategoryBlur = async () => {
    const clamped = clampVotesPerCategory(votesPerCategory);
    setVotesPerCategory(clamped);
    if (!yogaSettingsHydratedRef.current) return;
    await persistYogaSettings({ votesPerCategory: clamped }, "votes");
  };

  // --- submit all ---
  const handleSubmit = async () => {
    const trimmedRuleGroups = {};
    const trimmedTitles = {};
    for (const { key, titleKey, label } of RULE_GROUPS) {
      const title = String(ruleTitles[titleKey] ?? label).trim();
      if (!title) {
        Swal.fire(
          "Missing title",
          `Please enter a title for the "${label}" section.`,
          "warning"
        );
        return;
      }
      trimmedTitles[titleKey] = title.slice(0, TITLE_MAX_LEN);

      const trimmed = (ruleGroups[key] || []).map((r) => String(r ?? "").trim());
      if (trimmed.length === 0 || trimmed.some((r) => r === "")) {
        Swal.fire(
          "Incomplete rules",
          `Please add text to every rule in "${title || label}", or remove empty rows using the × button before saving.`,
          "warning"
        );
        return;
      }
      trimmedRuleGroups[key] = trimmed;
    }
    const trimmedUploadRules = gameUploadRules.map((r) => String(r ?? "").trim());
    if (
      trimmedUploadRules.length === 0 ||
      trimmedUploadRules.some((r) => r === "")
    ) {
      Swal.fire(
        "Incomplete upload rules",
        "Please add text to every game upload rule, or remove empty rows before saving.",
        "warning"
      );
      return;
    }
    if (badgesShareStatus && badgesShareMultiple) {
      const parsedLimit = Number.parseInt(String(badgesShareLimit ?? ""), 10);
      if (
        !Number.isFinite(parsedLimit) ||
        parsedLimit < MIN_BADGE_SHARE_LIMIT ||
        parsedLimit > MAX_BADGE_SHARE_LIMIT
      ) {
        const msg = `Upload limit must be between ${MIN_BADGE_SHARE_LIMIT} and ${MAX_BADGE_SHARE_LIMIT}.`;
        setBadgesShareLimitError(msg);
        Swal.fire("Invalid upload limit", msg, "warning");
        return;
      }
    }
    const normalizedBadgeShareLimit = clampBadgeShareLimit(badgesShareLimit);
    const normalizedVotes = clampVotesPerCategory(votesPerCategory);
    const normalizedSectionStyles = normalizeAllSectionStyles(sectionStyles);
    setSectionStyles(normalizedSectionStyles);

    const payload = {
      data: {
        ...trimmedRuleGroups,
        ...trimmedTitles,
        game_upload_rules: trimmedUploadRules,
        custom_text_thank_you_page: thankYouPage,
        text_color: colors.text_color,
        button_color: colors.button_color,
        button_backgroundcolor: colors.button_backgroundcolor,
        quiz_question_text_color: colors.quiz_question_text_color,
        quiz_question_background_color: colors.quiz_question_background_color,
        quiz_option_text_color: colors.quiz_option_text_color,
        quiz_option_background_color: colors.quiz_option_background_color,
        rules_text_color: colors.rules_text_color,
        section_styles: normalizedSectionStyles,
        voting_status: votingOpen ? "open" : "closed",
        votes_per_category: normalizedVotes,
        gallery_display: galleryDisplay ? "true" : "false",
        mosaic_enabled: mosaicEnabled ? "true" : "false",
        badges_share_status: badgesShareStatus ? "true" : "false",
        badges_share_multiple: badgesShareMultiple ? "true" : "false",
        badges_share_limit: badgesShareMultiple ? normalizedBadgeShareLimit : 1,
        upload_status: uploadOpen ? "open" : "closed",
        upload_count: uploadOpen ? clampUploadCount(uploadCount) : MIN_UPLOAD_COUNT,
      },
      currentTheme,
    };

    try {
      await updateThemeData({ payload, token: adminToken });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      clearAdminDirty();
      setTimeout(() => navigate("/admin/customize-spot"), 500);
    } catch (e) {
      Swal.fire(
        "Error",
        e.message || "Failed to save. Please try again.",
        "error"
      );
    }
  };

  return (
    <>
      <ColorPallet isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />

      <div className="rules-page-new">
        <div className="admin-tabs-holder">
          <div className="rules-page-columns">
            <div className="rules-page-center" data-tour="rules-game-settings">
              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-trophy"></i> &nbsp; Game Settings
                </div>

                <h3 className="custom-rules-title" data-tour="rules-custom-rules">Custom rules</h3>
                <div className="code" style={{ marginBottom: 12 }}>
                  Edit each section title and its rules. Title and rules typography are independent per section.
                  Game upload RULES (player upload step) are edited separately below the colors.
                </div>
                {RULE_GROUPS.map(({ key, titleKey, label, styleId }) => {
                  const rules = ruleGroups[key] || [""];
                  const sectionTitle = ruleTitles[titleKey] ?? label;
                  const styles = sectionStyles[styleId] || emptySectionStyles()[styleId];
                  return (
                    <div
                      className="rule-group"
                      key={key}
                      data-tour={`rules-group-${key}`}
                    >
                      <div className="form-group-v1">
                        <label>Section title</label>
                        <input
                          className="rule-input"
                          type="text"
                          value={sectionTitle}
                          maxLength={TITLE_MAX_LEN}
                          placeholder={label}
                          onChange={(e) =>
                            handleRuleTitleChange(titleKey, e.target.value)
                          }
                        />
                        <TextStyleControls
                          label="Title typography"
                          style={styles.title}
                          previewText={sectionTitle || label}
                          onChange={(next) => updateSectionStyle(styleId, "title", next)}
                          onSizeBlur={(sizeKey) =>
                            commitSectionFontSize(styleId, "title", sizeKey)
                          }
                        />
                      </div>
                      <label className="rules-body-label">Rules</label>
                      {rules.map((rule, i) => (
                        <div className="rule-row" key={`${key}-${i}`}>
                          <span className="rule-number">{i + 1}.</span>
                          <input
                            className="rule-input"
                            type="text"
                            placeholder="Enter rule"
                            value={rule}
                            maxLength={RULE_MAX_LEN}
                            onChange={(e) =>
                              handleRuleChange(key, i, e.target.value)
                            }
                          />
                          <button
                            className="remove-btn"
                            type="button"
                            onClick={() => handleRemoveRule(key, i)}
                          >
                            &times;
                          </button>
                        </div>
                      ))}
                      <TextStyleControls
                        label="Rules typography"
                        style={styles.rules}
                        previewText={rules.find((r) => String(r).trim()) || "Rule text preview"}
                        onChange={(next) => updateSectionStyle(styleId, "rules", next)}
                        onSizeBlur={(sizeKey) =>
                          commitSectionFontSize(styleId, "rules", sizeKey)
                        }
                      />
                      <div className="rules-button-container">
                        <div>
                          <button
                            className="add-btn"
                            type="button"
                            onClick={() => handleAddRule(key)}
                          >
                            + Add New Rule
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div className="code">
                  * Keep {RULE_MAX_COUNT} rules at the most per section.
                  <br />
                  * At least 1 rule is required in each section.
                  <br />
                  * Character limit of each rule is {RULE_MAX_LEN}
                  <br />
                </div>
              </div>
            </div>

            <div className="rules-page-right" data-tour="rules-customization">
              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-palette"></i> &nbsp; Customization
                </div>
                <div className="form-group-v1 rules-toggle-row" data-tour="rules-voting">
                  <label>Category voting</label>
                  <div className="rules-toggle-control">
                    <Switch
                      checked={votingOpen}
                      onChange={handleVotingChange}
                      disabled={savingSetting != null}
                      onColor="#7c3aed"
                      offColor="#cbd5e1"
                      height={22}
                      width={44}
                    />
                    <span>
                      {savingSetting === "voting"
                        ? "Saving…"
                        : votingOpen
                          ? "Open"
                          : "Closed"}
                    </span>
                  </div>
                  <div className="code">
                    When open, gallery cards show a Vote button. Reactions stay available.
                  </div>
                </div>
                <div className="form-group-v1" data-tour="rules-votes-per-category">
                  <label>Votes per category</label>
                  <input
                    type="number"
                    min={MIN_VOTES_PER_CATEGORY}
                    max={MAX_VOTES_PER_CATEGORY}
                    value={votesPerCategory}
                    onChange={(e) => handleVotesPerCategoryChange(e.target.value)}
                    onBlur={handleVotesPerCategoryBlur}
                    disabled={savingSetting != null}
                  />
                  <div className="code">
                    How many entries a player can vote for within one category (1–{MAX_VOTES_PER_CATEGORY}).
                    {savingSetting === "votes" ? " Saving…" : ""}
                  </div>
                </div>
                <div className="form-group-v1 rules-toggle-row" data-tour="rules-gallery-display">
                  <label>Display gallery</label>
                  <div className="rules-toggle-control">
                    <Switch
                      checked={galleryDisplay}
                      onChange={handleGalleryDisplayChange}
                      disabled={savingSetting != null}
                      onColor="#7c3aed"
                      offColor="#cbd5e1"
                      height={22}
                      width={44}
                    />
                    <span>
                      {savingSetting === "gallery"
                        ? "Saving…"
                        : galleryDisplay
                          ? "True"
                          : "False"}
                    </span>
                  </div>
                  <div className="code">
                    If false, players see a moderation thank-you message instead of uploads.
                    Saves immediately (same as Category voting).
                  </div>
                </div>
                {/* <div className="form-group-v1 rules-toggle-row">
                  <label>Player upload</label>
                  <div className="rules-toggle-control">
                    <Switch
                      checked={uploadOpen}
                      onChange={handleUploadChange}
                      disabled={savingSetting != null}
                      onColor="#7c3aed"
                      offColor="#cbd5e1"
                      height={22}
                      width={44}
                    />
                    <span>
                      {savingSetting === "upload"
                        ? "Saving…"
                        : uploadOpen
                          ? "Open"
                          : "Closed"}
                    </span>
                  </div>
                  <div className="code">
                    When closed, players finish poses and go straight to the gallery.
                  </div>
                </div> */}
                {/* {uploadOpen ? (
                  <div className="form-group-v1">
                    <label>Upload count per player</label>
                    <input
                      type="number"
                      min={MIN_UPLOAD_COUNT}
                      max={MAX_UPLOAD_COUNT}
                      value={uploadCount}
                      onChange={(e) => handleUploadCountChange(e.target.value)}
                      onBlur={handleUploadCountBlur}
                      disabled={savingSetting != null}
                    />
                    <div className="code">
                      * How many photos/videos each player may upload (default 1, max {MAX_UPLOAD_COUNT}).
                    </div>
                  </div>
                ) : null} */}
                <div className="form-group-v1">
                  <button
                    type="button"
                    className="rules-manage-uploads-btn"
                    onClick={() => navigate("/admin/uploads")}
                  >
                    Manage gallery uploads
                  </button>
                </div>
                <div className="form-group-v1">
                  <button
                    type="button"
                    className="rules-manage-uploads-btn"
                    onClick={() => navigate("/admin/jury")}
                  >
                    Manage jury members
                  </button>
                </div>
                <div className="form-group-v1 rules-toggle-row" data-tour="rules-mosaic">
                  <label>Enable mosaic</label>
                  <div className="rules-toggle-control">
                    <Switch
                      checked={mosaicEnabled}
                      onChange={handleMosaicEnabledChange}
                      disabled={savingSetting != null}
                      onColor="#7c3aed"
                      offColor="#cbd5e1"
                      height={22}
                      width={44}
                    />
                    <span>
                      {savingSetting === "mosaic"
                        ? "Saving…"
                        : mosaicEnabled
                          ? "Enabled"
                          : "Disabled"}
                    </span>
                  </div>
                  <div className="code">
                    When enabled, players see a “See Mosaic” button on the gallery. Saves immediately.
                  </div>
                </div>
                <div className="form-group-v1">
                  <button
                    type="button"
                    className="rules-manage-uploads-btn"
                    onClick={() => navigate("/admin/mosaic")}
                    disabled={!mosaicEnabled}
                    style={mosaicEnabled ? undefined : { opacity: 0.55, cursor: "not-allowed" }}
                  >
                    Mosaic content settings
                  </button>
                  <div className="code">
                    Set mosaic word and desktop/mobile font sizes
                    {mosaicEnabled ? "" : " (enable mosaic first)"}.
                  </div>
                </div>
                <div className="form-group-v1 rules-toggle-row" data-tour="rules-upload-sharing">
                  <label>Form upload button </label>
                  <div className="rules-toggle-control">
                    <Switch
                      checked={badgesShareStatus}
                      onChange={(checked) => {
                        setBadgesShareStatus(checked);
                        markDirty();
                      }}
                      onColor="#7c3aed"
                      offColor="#cbd5e1"
                      height={22}
                      width={44}
                    />
                    <span>{badgesShareStatus ? "Enabled" : "Disabled"}</span>
                  </div>
                  <div className="code">When disabled, players cannot share uploads.</div>
                </div>
                <div className="form-group-v1 rules-toggle-row">
                  <label>Multiple upload</label>
                  <div className="rules-toggle-control">
                    <Switch
                      checked={badgesShareMultiple}
                      onChange={(checked) => {
                        setBadgesShareMultiple(checked);
                        markDirty();
                      }}
                      disabled={!badgesShareStatus}
                      onColor="#7c3aed"
                      offColor="#cbd5e1"
                      height={22}
                      width={44}
                    />
                    <span>{badgesShareMultiple ? "Yes" : "No"}</span>
                  </div>
                  <div className="code">Allow sharing more than one upload at a time.</div>
                </div>
                {badgesShareStatus && badgesShareMultiple ? (
                  <div className="form-group-v1">
                    <label>Upload limit</label>
                    <input
                      type="number"
                      min={MIN_BADGE_SHARE_LIMIT}
                      max={MAX_BADGE_SHARE_LIMIT}
                      value={badgesShareLimit}
                      onChange={(e) => handleBadgeShareLimitChange(e.target.value)}
                      onBlur={handleBadgeShareLimitBlur}
                      aria-invalid={badgesShareLimitError ? "true" : "false"}
                    />
                    {badgesShareLimitError ? (
                      <div className="code" style={{ color: "#dc2626" }}>
                        {badgesShareLimitError}
                      </div>
                    ) : (
                      <div className="code">
                        * Minimum {MIN_BADGE_SHARE_LIMIT} and maximum {MAX_BADGE_SHARE_LIMIT} uploads total (any categories).
                      </div>
                    )}
                  </div>
                ) : null}
                <div className="pallet-holder" data-tour="rules-colors">
                  <h3 className="custom-rules-title" style={{ marginBottom: 8 }}>
                    Colors
                  </h3>
                  <div className="code" style={{ marginBottom: 10 }}>
                    Button and text colors. Section title/rules fonts are set in each rules block above.
                  </div>
                  <div className="pallet-row">
                    <div className="pallet-input">
                      <div className="color-holder">
                        <input
                          type="color"
                          className="pallet-swatch"
                          value={colors.text_color}
                          onChange={handleColorChange("text_color")}
                        />
                        <label>Text Color</label>
                      </div>
                      <div className="color-code">{colors.text_color}</div>
                    </div>
                  </div>
                  <div className="pallet-row">
                    <div className="pallet-input">
                      <div className="color-holder">
                        <input
                          type="color"
                          className="pallet-swatch"
                          value={colors.button_color}
                          onChange={handleColorChange("button_color")}
                        />
                        <label>Button Text Color</label>
                      </div>
                      <div className="color-code">{colors.button_color}</div>
                    </div>
                  </div>
                  <div className="pallet-row">
                    <div className="pallet-input">
                      <div className="color-holder">
                        <input
                          type="color"
                          className="pallet-swatch"
                          value={colors.button_backgroundcolor}
                          onChange={handleColorChange("button_backgroundcolor")}
                        />
                        <label>Button Background Color</label>
                      </div>
                      <div className="color-code">{colors.button_backgroundcolor}</div>
                    </div>
                  </div>
                </div>

                <div className="rule-group" data-tour="rules-upload-rules" style={{ marginTop: 20 }}>
                  <h3 className="custom-rules-title">Game upload RULES</h3>
                  <div className="code" style={{ marginBottom: 10 }}>
                    Shown under RULES on the player game upload step.
                  </div>
                  <TextStyleControls
                    label="Heading typography"
                    style={(sectionStyles.game_upload || emptySectionStyles().game_upload).title}
                    previewText="Rules"
                    onChange={(next) => updateSectionStyle("game_upload", "title", next)}
                    onSizeBlur={(sizeKey) =>
                      commitSectionFontSize("game_upload", "title", sizeKey)
                    }
                  />
                  {gameUploadRules.map((rule, i) => (
                    <div className="rule-row" key={`upload-rule-${i}`}>
                      <span className="rule-number">{i + 1}.</span>
                      <input
                        className="rule-input"
                        type="text"
                        placeholder="Enter upload rule"
                        value={rule}
                        maxLength={RULE_MAX_LEN}
                        onChange={(e) =>
                          handleGameUploadRuleChange(i, e.target.value)
                        }
                      />
                      <button
                        className="remove-btn"
                        type="button"
                        onClick={() => handleRemoveGameUploadRule(i)}
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                  <TextStyleControls
                    label="Rules typography"
                    style={(sectionStyles.game_upload || emptySectionStyles().game_upload).rules}
                    previewText={
                      gameUploadRules.find((r) => String(r).trim()) || "Rule text preview"
                    }
                    onChange={(next) => updateSectionStyle("game_upload", "rules", next)}
                    onSizeBlur={(sizeKey) =>
                      commitSectionFontSize("game_upload", "rules", sizeKey)
                    }
                  />
                  <div className="rules-button-container">
                    <div>
                      <button
                        className="add-btn"
                        type="button"
                        onClick={handleAddGameUploadRule}
                      >
                        + Add New Rule
                      </button>
                    </div>
                  </div>
                  <div className="code">
                    * Keep {RULE_MAX_COUNT} rules at the most.
                    <br />
                    * At least 1 rule is required.
                    <br />
                    * Character limit of each rule is {RULE_MAX_LEN}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="rules-action-holder" data-tour="rules-save">
            <button className="save-and-continue" onClick={handleSubmit}>
              Save & Continue
            </button>
           
          </div>
        </div>
      </div>
    </>
  );
};

export default Rules;
